import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";

// Two-context split: AuthState changes per session, AuthActions are stable.
// useAuth() preserves the existing combined API for call sites.
const AuthStateContext = createContext({ user: null, ready: false });
const AuthActionsContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null); // null = checking, false = logged-out
  const [ready, setReady] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      return data;
    } catch (err) {
      // 401 here is the expected unauthenticated path. Per-page handlers will
      // surface other failures to the user; refresh() only manages local state.
      void err;
      setUser(false);
      return null;
    } finally {
      setReady(true);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const login = useCallback(async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    setUser(data.user);
    return data.user;
  }, []);

  const register = useCallback(async (email, password, name) => {
    const { data } = await api.post("/auth/register", { email, password, name });
    setUser(data.user);
    return data.user;
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch (err) {
      // Backend logout failures shouldn't block local sign-out — clear state regardless.
      void err;
    }
    setUser(false);
  }, []);

  const updateProfile = useCallback(async (patch) => {
    const { data } = await api.patch("/users/me", patch);
    setUser(data);
    return data;
  }, []);

  const state = useMemo(() => ({ user, ready }), [user, ready]);
  const actions = useMemo(
    () => ({ refresh, login, register, logout, updateProfile, formatApiErrorDetail }),
    [refresh, login, register, logout, updateProfile]
  );

  return (
    <AuthStateContext.Provider value={state}>
      <AuthActionsContext.Provider value={actions}>
        {children}
      </AuthActionsContext.Provider>
    </AuthStateContext.Provider>
  );
};

export const useAuthState = () => useContext(AuthStateContext);

export const useAuthActions = () => {
  const ctx = useContext(AuthActionsContext);
  if (!ctx) throw new Error("useAuthActions must be used inside AuthProvider");
  return ctx;
};

// Backwards-compatible combined hook used across the app.
export const useAuth = () => {
  const state = useAuthState();
  const actions = useAuthActions();
  return useMemo(() => ({ ...state, ...actions }), [state, actions]);
};
